import type { Snapshot } from '@ember-data/legacy-compat/-private';
import { EmbeddedRecordsMixin } from '@ember-data/serializer/rest';
import type Store from '@ember-data/store';
import type { ModelSchema } from '@ember-data/store/types';
import ApplicationSerializer from 'lumen/serializers/application';
import {
  camelizeSavingStatus,
  type SavingStatusRawResponse,
  type SavingStatusResponse,
} from 'lumen/services/central-mapping/saving';

// Meta of the POST response (see `updateMappingTask` in `services/central-mapping.ts`). Only `status` is read.
export interface MappingEntriesRawResponseMeta {
  status: SavingStatusRawResponse;
}

// Meta of the (EmberData) query response, where the pagination fields are flat.
// The POST response meta (`MappingEntriesRawResponseMeta`) has a different shape.
interface MappingEntriesQueryRawResponseMeta {
  count: number;
  page: number;
  page_count: number;
  page_size: number;
  // Only sent for market mapping, and not used for hotel mapping.
  status?: SavingStatusRawResponse;
}

export interface MappingEntriesResponseMeta {
  paginationMeta?: {
    endIndex: number;
    page: number;
    pageCount: number;
    pageSize: number;
    startIndex: number;
    totalNoOfItems: number;
  };
  // Status is only sent for market mapping, not hotel mapping.
  status?: SavingStatusResponse;
}

export const MAPPING_ENTRY_ATTRS = {
  userCategory: 'user_category_id',
  autoCategory: 'auto_category_id',
  subscription: 'subscription_id',
};

// eslint-disable-next-line @typescript-eslint/no-unsafe-call
export default class CentralMappingMappingEntrySerializer extends ApplicationSerializer.extend(
  EmbeddedRecordsMixin,
) {
  attrs = MAPPING_ENTRY_ATTRS;

  extractMeta(
    store: Store,
    modelClass: ModelSchema,
    payload: Record<string, unknown>,
  ): MappingEntriesResponseMeta {
    // `ApplicationSerializer.extractMeta` is untyped. The shape is what the mapping_entries query endpoint sends.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const rawMeta = super.extractMeta(
      store,
      modelClass,
      payload,
    ) as MappingEntriesQueryRawResponseMeta;
    const { count, page, page_count, page_size, status } = rawMeta;

    const meta: MappingEntriesResponseMeta = {
      paginationMeta: {
        startIndex: (page - 1) * page_size + 1,
        endIndex: Math.min(page * page_size, count),
        totalNoOfItems: count,
        pageSize: page_size,
        page,
        pageCount: page_count,
      },
    };

    // `status` is optional, and only exposed while a save is in progress. A status that is not
    // in progress is dropped on purpose, so consumers see `undefined` instead of `inProgress: false`.
    if (status?.in_progress) {
      meta.status = camelizeSavingStatus(status);
    }

    return meta;
  }

  modelNameFromPayloadKey(key: string): string {
    if (key === 'data') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      return super.modelNameFromPayloadKey('central-mapping/mapping-entry');
    }

    return key;
  }

  serialize(snapshot: Snapshot, options: object): Record<string, unknown> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const payload = super.serialize(snapshot, options);

    // Don't include the `auto_category_id`. They are not meant to be changed in the frontend, and backend ignores it anyway.
    if ('auto_category_id' in payload) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      delete payload.auto_category_id;
    }

    // backend expects `subscription_id` and `id` to be an integer
    if ('subscription_id' in payload) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      payload.subscription_id = Number(payload.subscription_id);
    }
    if ('id' in payload) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      payload.id = Number(payload.id);
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return payload;
  }
}
